import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from "@/components/ui/use-toast";
import { PurchaseRequisition, PRFormData, ProcurementKPIs } from '@/types/procurement';

export const usePurchaseRequisitions = () => {
  const [purchaseRequisitions, setPurchaseRequisitions] = useState<PurchaseRequisition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all purchase requisitions with related data
  const fetchPurchaseRequisitions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: prData, error: prError } = await supabase
        .from('purchase_requisition')
        .select(`
          *,
          pr_lines:pr_line(
            *,
            item:inventory_items(id, code, name, category, unit),
            preferred_supplier:suppliers(id, name, code)
          )
        `)
        .order('created_at', { ascending: false });

      if (prError) throw prError;

      // Fetch requester details separately
      const requesterIds = [...new Set(prData?.map(pr => pr.requester_id).filter(Boolean) || [])];
      let usersMap: Record<string, { id: string; full_name: string; email: string }> = {};
      
      if (requesterIds.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', requesterIds);
        
        usersMap = (usersData || []).reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {} as Record<string, { id: string; full_name: string; email: string }>);
      }

      const data = prData?.map(pr => ({
        ...pr,
        requester: pr.requester_id ? usersMap[pr.requester_id] || null : null,
      }));

      const error = null;

      if (error) throw error;
      
      // Transform the data to match our interface
      const transformedData = data?.map(pr => ({
        ...pr,
        pr_lines: pr.pr_lines || [],
      })) || [];

      setPurchaseRequisitions(transformedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch purchase requisitions';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Get PR statistics/KPIs
  const getKPIs = useCallback((): ProcurementKPIs => {
    return {
      draft: purchaseRequisitions.filter(pr => pr.status === 'DRAFT').length,
      submitted: purchaseRequisitions.filter(pr => pr.status === 'SUBMITTED').length,
      ordered: purchaseRequisitions.filter(pr => pr.status === 'ORDERED').length,
      closed: purchaseRequisitions.filter(pr => pr.status === 'CLOSED').length,
    };
  }, [purchaseRequisitions]);

  // Create new purchase requisition
  const createPurchaseRequisition = useCallback(async (prData: PRFormData): Promise<string | null> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Generate PR code
      const { data: codeData, error: codeError } = await supabase
        .rpc('generate_procurement_code', { table_type: 'PR' });

      if (codeError) throw codeError;

      // Insert purchase requisition
      const { data: pr, error: prError } = await supabase
        .from('purchase_requisition')
        .insert([{
          code: codeData,
          requester_id: user.id,
          status: 'DRAFT',
          needed_by: prData.needed_by || null,
          remarks: prData.remarks || null,
        }])
        .select('id')
        .single();

      if (prError) throw prError;

      // Insert PR lines if any
      if (prData.lines.length > 0) {
        const prLines = prData.lines.map(line => ({
          pr_id: pr.id,
          item_id: line.item_id,
          qty: line.qty,
          uom: line.uom,
          preferred_supplier_id: line.preferred_supplier_id && line.preferred_supplier_id !== 'none' ? line.preferred_supplier_id : null,
        }));

        const { error: linesError } = await supabase
          .from('pr_line')
          .insert(prLines);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Purchase requisition ${codeData} created successfully`,
      });

      // Refresh the list
      await fetchPurchaseRequisitions();
      return pr.id;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create purchase requisition';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseRequisitions]);

  // Update purchase requisition
  const updatePurchaseRequisition = useCallback(async (id: string, prData: PRFormData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Update purchase requisition
      const { error: prError } = await supabase
        .from('purchase_requisition')
        .update({
          needed_by: prData.needed_by || null,
          remarks: prData.remarks || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (prError) throw prError;

      // Delete existing lines and insert new ones
      const { error: deleteError } = await supabase
        .from('pr_line')
        .delete()
        .eq('pr_id', id);

      if (deleteError) throw deleteError;

      // Insert new lines if any
      if (prData.lines.length > 0) {
        const prLines = prData.lines.map(line => ({
          pr_id: id,
          item_id: line.item_id,
          qty: line.qty,
          uom: line.uom,
          preferred_supplier_id: line.preferred_supplier_id && line.preferred_supplier_id !== 'none' ? line.preferred_supplier_id : null,
        }));

        const { error: linesError } = await supabase
          .from('pr_line')
          .insert(prLines);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: 'Purchase requisition updated successfully',
      });

      // Refresh the list
      await fetchPurchaseRequisitions();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update purchase requisition';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseRequisitions]);

  // Submit purchase requisition (change status from DRAFT to SUBMITTED)
  const submitPurchaseRequisition = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('purchase_requisition')
        .update({
          status: 'SUBMITTED',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'DRAFT'); // Only allow submitting from DRAFT status

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Purchase requisition submitted successfully',
      });

      // Refresh the list
      await fetchPurchaseRequisitions();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit purchase requisition';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseRequisitions]);

  // Delete purchase requisition (only if status is DRAFT)
  const deletePurchaseRequisition = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if PR is in DRAFT status
      const { data: pr, error: checkError } = await supabase
        .from('purchase_requisition')
        .select('status, code')
        .eq('id', id)
        .single();

      if (checkError) throw checkError;

      if (pr.status !== 'DRAFT') {
        throw new Error('Only draft purchase requisitions can be deleted');
      }

      // Delete the PR (lines will be deleted automatically due to CASCADE)
      const { error } = await supabase
        .from('purchase_requisition')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Purchase requisition ${pr.code} deleted successfully`,
      });

      // Refresh the list
      await fetchPurchaseRequisitions();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete purchase requisition';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPurchaseRequisitions]);

  // Get single purchase requisition by ID
  const getPurchaseRequisition = useCallback(async (id: string): Promise<PurchaseRequisition | null> => {
    try {
      const { data: prData, error: prError } = await supabase
        .from('purchase_requisition')
        .select(`
          *,
          pr_lines:pr_line(
            *,
            item:inventory_items(id, code, name, category, unit),
            preferred_supplier:suppliers(id, name, code)
          )
        `)
        .eq('id', id)
        .single();

      if (prError) throw prError;

      // Fetch requester details separately
      let requester = null;
      if (prData.requester_id) {
        const { data: userData } = await supabase
          .from('users')
          .select('id, full_name, email')
          .eq('id', prData.requester_id)
          .single();
        requester = userData;
      }

      // Transform the data
      return {
        ...prData,
        requester,
        pr_lines: prData.pr_lines || [],
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch purchase requisition';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    }
  }, []);

  // Load purchase requisitions on hook initialization
  useEffect(() => {
    fetchPurchaseRequisitions();
  }, [fetchPurchaseRequisitions]);

  return {
    purchaseRequisitions,
    loading,
    error,
    getKPIs,
    fetchPurchaseRequisitions,
    createPurchaseRequisition,
    updatePurchaseRequisition,
    submitPurchaseRequisition,
    deletePurchaseRequisition,
    getPurchaseRequisition,
  };
};
